import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '@src/store';
import type { InputStatus } from '@src/types/input_status';
import { createSelector } from 'reselect';

/**
 * 統合入力ステータススライス
 * Resume用とRirekisho用の入力完了状態を1つのスライスで管理
 */

interface RirekishoInputStatus {
  basicInfo: boolean;
  photo: boolean;
  currentAddress: boolean;
  contact: boolean;
  history: boolean;
  license: boolean;
  motivation: boolean;
  personalRequests: boolean;
  signature: boolean;
}

type UnifiedInputStatusState = {
  resume: InputStatus;
  rirekisho: RirekishoInputStatus;
};

const initialState: UnifiedInputStatusState = {
  resume: {
    isContactComplete: false,
    isSkillComplete: false,
    isHistoryComplete: false,
    isProjectComplete: false,
    isHistoryCommentCheked: false,
    isCareerSummaryComplete: false,
    isNextCareerComplete: false,
    isPrivateActivityChecked: false,
    isEducationalBackgroundComplete: false,
    status: 0,
  },
  rirekisho: {
    basicInfo: false,
    photo: false,
    currentAddress: false,
    contact: false,
    history: false,
    license: false,
    motivation: false,
    personalRequests: false,
    signature: false,
  },
};

const unifiedInputStatusSlice = createSlice({
  name: 'unifiedInputStatus',
  initialState,
  reducers: {
    // Resume用のreducers
    updateResumeContactComplete(state: UnifiedInputStatusState, action: PayloadAction<boolean>) {
      state.resume.isContactComplete = action.payload;
    },
    updateResumeSkillComplete(state: UnifiedInputStatusState, action: PayloadAction<boolean>) {
      state.resume.isSkillComplete = action.payload;
    },
    updateResumeHistoryComplete(state: UnifiedInputStatusState, action: PayloadAction<boolean>) {
      state.resume.isHistoryComplete = action.payload;
    },
    updateResumeProjectComplete(state: UnifiedInputStatusState, action: PayloadAction<boolean>) {
      state.resume.isProjectComplete = action.payload;
    },
    updateResumeCareerSummaryComplete(
      state: UnifiedInputStatusState,
      action: PayloadAction<boolean>
    ) {
      state.resume.isCareerSummaryComplete = action.payload;
    },
    updateResumeNextCareerComplete(state: UnifiedInputStatusState, action: PayloadAction<boolean>) {
      state.resume.isNextCareerComplete = action.payload;
    },
    updateResumePrivateActivityChecked(
      state: UnifiedInputStatusState,
      action: PayloadAction<boolean>
    ) {
      state.resume.isPrivateActivityChecked = action.payload;
    },
    updateResumeHistoryCommentChecked(
      state: UnifiedInputStatusState,
      action: PayloadAction<boolean>
    ) {
      state.resume.isHistoryCommentCheked = action.payload;
    },
    updateResumeEducationalBackgroundComplete(
      state: UnifiedInputStatusState,
      action: PayloadAction<boolean>
    ) {
      state.resume.isEducationalBackgroundComplete = action.payload;
    },
    updateResumeInputStatus(state: UnifiedInputStatusState, action: PayloadAction<InputStatus>) {
      Object.assign(state.resume, action.payload);
    },

    // Rirekisho用のreducers
    updateRirekishoInputStatus(
      state: UnifiedInputStatusState,
      action: PayloadAction<Partial<RirekishoInputStatus>>
    ) {
      state.rirekisho = { ...state.rirekisho, ...action.payload };
    },
    setRirekishoSectionComplete(
      state: UnifiedInputStatusState,
      action: PayloadAction<{ section: keyof RirekishoInputStatus; complete: boolean }>
    ) {
      const { section, complete } = action.payload;
      state.rirekisho[section] = complete;
    },
    resetRirekishoInputStatus(state: UnifiedInputStatusState) {
      state.rirekisho = initialState.rirekisho;
    },
  },
});

export const {
  // Resume actions
  updateResumeContactComplete,

  updateResumeInputStatus,
  // Rirekisho actions
  updateRirekishoInputStatus,
  setRirekishoSectionComplete,
} = unifiedInputStatusSlice.actions;

// 後方互換性のためのエイリアス
const _updateContactComplete = unifiedInputStatusSlice.actions.updateResumeContactComplete;
export const updateSkillComplete = unifiedInputStatusSlice.actions.updateResumeSkillComplete;
export const updateHistoryComplete = unifiedInputStatusSlice.actions.updateResumeHistoryComplete;
export const updateProjectComplete = unifiedInputStatusSlice.actions.updateResumeProjectComplete;
export const updateCareerSummaryComplete =
  unifiedInputStatusSlice.actions.updateResumeCareerSummaryComplete;
export const updateNextCareerComplete =
  unifiedInputStatusSlice.actions.updateResumeNextCareerComplete;
export const updatePrivateActivityChecked =
  unifiedInputStatusSlice.actions.updateResumePrivateActivityChecked;
export const updateHistoryCommentChecked =
  unifiedInputStatusSlice.actions.updateResumeHistoryCommentChecked;
export const updateEducationalBackgroundComplete =
  unifiedInputStatusSlice.actions.updateResumeEducationalBackgroundComplete;
const _updateInputStatus = unifiedInputStatusSlice.actions.updateResumeInputStatus;

// Selectors
export const selectResumeInputStatus = (state: RootState) => state.unifiedInputStatus.resume;
const selectRirekishoInputStatus = (state: RootState) => state.unifiedInputStatus.rirekisho;
// 後方互換性のためのエイリアス
const _selectInputStatus = (state: RootState) => state.unifiedInputStatus.resume;

// Resume用の計算セレクター
const selectInputStatusFields = createSelector([selectResumeInputStatus], (inputStatus) => ({
  isContactComplete: inputStatus.isContactComplete,
  isCareerSummaryComplete: inputStatus.isCareerSummaryComplete,
  isHistoryComplete: inputStatus.isHistoryComplete,
  isSkillComplete: inputStatus.isSkillComplete,
  isProjectComplete: inputStatus.isProjectComplete,
  isHistoryCommentCheked: inputStatus.isHistoryCommentCheked,
  isNextCareerComplete: inputStatus.isNextCareerComplete,
  isEducationalBackgroundComplete: inputStatus.isEducationalBackgroundComplete,
  isPrivateActivityChecked: inputStatus.isPrivateActivityChecked,
}));

const _selectCompletionPercentage = createSelector([selectInputStatusFields], (fields) => {
  const totalFields = 9;
  const completedCount = Object.values(fields).filter(Boolean).length;
  return (completedCount / totalFields) * 100;
});

const _selectCompletedSections = createSelector([selectInputStatusFields], (fields) => {
  const sections = [];
  if (fields.isContactComplete) {
    sections.push('contact');
  }
  if (fields.isSkillComplete) {
    sections.push('skill');
  }
  if (fields.isHistoryComplete) {
    sections.push('history');
  }
  if (fields.isProjectComplete) {
    sections.push('project');
  }
  if (fields.isHistoryCommentCheked) {
    sections.push('historyComment');
  }
  if (fields.isCareerSummaryComplete) {
    sections.push('careerSummary');
  }
  if (fields.isNextCareerComplete) {
    sections.push('nextCareer');
  }
  if (fields.isPrivateActivityChecked) {
    sections.push('privateActivity');
  }
  if (fields.isEducationalBackgroundComplete) {
    sections.push('educationalBackground');
  }
  return sections;
});

const _selectIncompleteSections = createSelector([selectInputStatusFields], (fields) => {
  const sections = [];
  if (!fields.isContactComplete) {
    sections.push('contact');
  }
  if (!fields.isSkillComplete) {
    sections.push('skill');
  }
  if (!fields.isHistoryComplete) {
    sections.push('history');
  }
  if (!fields.isProjectComplete) {
    sections.push('project');
  }
  if (!fields.isHistoryCommentCheked) {
    sections.push('historyComment');
  }
  if (!fields.isCareerSummaryComplete) {
    sections.push('careerSummary');
  }
  if (!fields.isNextCareerComplete) {
    sections.push('nextCareer');
  }
  if (!fields.isPrivateActivityChecked) {
    sections.push('privateActivity');
  }
  if (!fields.isEducationalBackgroundComplete) {
    sections.push('educationalBackground');
  }
  return sections;
});

const selectResumeInputStatusFields = createSelector([selectResumeInputStatus], (inputStatus) => ({
  isContactComplete: inputStatus.isContactComplete,
  isCareerSummaryComplete: inputStatus.isCareerSummaryComplete,
  isHistoryComplete: inputStatus.isHistoryComplete,
  isSkillComplete: inputStatus.isSkillComplete,
  isProjectComplete: inputStatus.isProjectComplete,
  isHistoryCommentCheked: inputStatus.isHistoryCommentCheked,
  isNextCareerComplete: inputStatus.isNextCareerComplete,
  isEducationalBackgroundComplete: inputStatus.isEducationalBackgroundComplete,
  isPrivateActivityChecked: inputStatus.isPrivateActivityChecked,
}));

export const selectResumeCompletionPercentage = createSelector(
  [selectResumeInputStatusFields],
  (fields) => {
    const totalFields = 9;
    const completedCount = Object.values(fields).filter(Boolean).length;
    return (completedCount / totalFields) * 100;
  }
);

const _selectResumeCompletedSections = createSelector([selectResumeInputStatusFields], (fields) => {
  const sections = [];
  if (fields.isContactComplete) {
    sections.push('contact');
  }
  if (fields.isSkillComplete) {
    sections.push('skill');
  }
  if (fields.isHistoryComplete) {
    sections.push('history');
  }
  if (fields.isProjectComplete) {
    sections.push('project');
  }
  if (fields.isHistoryCommentCheked) {
    sections.push('historyComment');
  }
  if (fields.isCareerSummaryComplete) {
    sections.push('careerSummary');
  }
  if (fields.isNextCareerComplete) {
    sections.push('nextCareer');
  }
  if (fields.isPrivateActivityChecked) {
    sections.push('privateActivity');
  }
  if (fields.isEducationalBackgroundComplete) {
    sections.push('educationalBackground');
  }
  return sections;
});

const _selectResumeIncompleteSections = createSelector(
  [selectResumeInputStatusFields],
  (fields) => {
    const sections = [];
    if (!fields.isContactComplete) {
      sections.push('contact');
    }
    if (!fields.isSkillComplete) {
      sections.push('skill');
    }
    if (!fields.isHistoryComplete) {
      sections.push('history');
    }
    if (!fields.isProjectComplete) {
      sections.push('project');
    }
    if (!fields.isHistoryCommentCheked) {
      sections.push('historyComment');
    }
    if (!fields.isCareerSummaryComplete) {
      sections.push('careerSummary');
    }
    if (!fields.isNextCareerComplete) {
      sections.push('nextCareer');
    }
    if (!fields.isPrivateActivityChecked) {
      sections.push('privateActivity');
    }
    if (!fields.isEducationalBackgroundComplete) {
      sections.push('educationalBackground');
    }
    return sections;
  }
);

// Rirekisho用の計算セレクター
const _selectRirekishoCompletionPercentage = createSelector(
  [selectRirekishoInputStatus],
  (status) => {
    const totalFields = Object.keys(status).length;
    const completedCount = Object.values(status).filter(Boolean).length;
    return (completedCount / totalFields) * 100;
  }
);

export default unifiedInputStatusSlice.reducer;
